import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Layout, Code, ShoppingCart, Globe, Sparkles, RefreshCw, Shield } from 'lucide-react';
import { servicesData } from '../mockData';
import { Button } from './ui/button';

const ServicesGrid = ({ showAll = false }) => {
  const icons = {
    Layout,
    Code,
    ShoppingCart,
    Globe,
    Sparkles,
    RefreshCw,
    Shield,
  };

  const displayServices = showAll ? servicesData : servicesData.slice(0, 6);

  return (
    <section className="py-32 bg-white" data-testid="services-section">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20 max-w-3xl mx-auto"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mb-6 tracking-tight">
            Everything you need
          </h2>
          <p className="text-lg md:text-xl text-gray-500 leading-relaxed">
            Comprehensive web solutions designed to elevate your digital presence
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {displayServices.map((service, index) => {
            const Icon = icons[service.icon];
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                viewport={{ once: true }}
                whileHover={{ y: -6 }}
              >
                <Link
                  to={`/services/${service.slug}`}
                  className="group flex flex-col h-full bg-[#FAFAFA] hover:bg-white rounded-3xl p-8 transition-all duration-500 border border-transparent hover:border-gray-100 hover:shadow-xl"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-white group-hover:bg-[#C8102E] flex items-center justify-center mb-6 shadow-sm transition-all duration-500">
                    <Icon className="text-[#C8102E] group-hover:text-white transition-colors duration-500" size={26} />
                  </div>
                  <h3 className="text-2xl font-bold text-[#1A1A1A] mb-3 tracking-tight">
                    {service.title}
                  </h3>
                  <p className="text-gray-500 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {service.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#C8102E] mr-3" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center text-[#C8102E] font-medium text-sm group-hover:gap-2 gap-1 transition-all duration-300 mt-auto">
                    <span>Learn more</span>
                    <ArrowRight size={16} />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* View All Button */}
        {!showAll && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link to="/services">
              <Button
                className="bg-[#1A1A1A] hover:bg-[#C8102E] text-white px-8 py-6 text-base rounded-full transition-all duration-300"
                data-testid="view-all-services-button"
              >
                View All Services
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default ServicesGrid;
